import {NextPage} from "next";
import React from "react";

import {fetchReasonBySlug} from "../../_services/api";
import {v5Pages} from "../../components/_common/_constants";
import {PracticeId} from "../../constants/practiceIds";
import {NextPageContextWithStore} from "../../store/types";
import {CareType, fetchCareEntry} from "../../utils/cms/cms";
import {isReasonDiscoverable} from "../../utils/isReasonDiscoverable";
import {redirectToLowercase} from "../../utils/redirectToLowercase";
import {Reason, Props as ReasonProps, getInitialProps as getInitialPropsReason} from "./reason";
import {Topic, Props as TopicProps, getInitialProps as getInitialPropsTopic} from "./topic";

type Props = ReasonProps | TopicProps;

const rednamedCareSlugs: Record<string, string> = {
  "allergy-symptoms-allergy-treatment": "allergy-symptoms",
  flu: "flu-shot",
  follow_up_pcp: "follow-up-pcp",
  "medication-refill": "medication-prescription-refill",
  mental_health_follow_up: "mental-health-follow-up",
  "pink-eye": "red-eye-pink-eye",
  "prescription-refill": "medication-prescription-refill",
  "routine-physical": "routine-physical-exam",
  "rx-refill": "medication-prescription-refill",
  rx: "medication-prescription-refill",
  "sinus-infection-symptoms": "sinus-infection",
  "sports-physical": "sports-physical-exam",
  "std-test": "sti-test",
  "urinary-tract-infection-uti-symptoms": "urinary-tract-infection",
  womens_health: "womens-health",
  "yeast-infection-symptoms": "yeast-infection",
};

const isTopicProps = (props: ReasonProps | TopicProps): props is TopicProps =>
  Boolean((props as TopicProps).content?.cares);

const CareItem: NextPage<Props> = (props: Props) =>
  isTopicProps(props) ? <Topic {...props} /> : <Reason {...(props as ReasonProps)} />;

CareItem.getInitialProps = async (context: NextPageContextWithStore): Promise<Props> => {
  const {res, query, req, asPath} = context;

  if (asPath && !!req && res) redirectToLowercase(asPath, res);

  const slug = query.slug as string;
  const type = query.type as string;
  const maybeNewSlug = rednamedCareSlugs[slug];

  const careEntry = await fetchCareEntry(slug);

  if (careEntry?.type === CareType.TOPIC) {
    return getInitialPropsTopic({content: careEntry.content, ...context});
  }

  const reason = await fetchReasonBySlug(maybeNewSlug || slug, PracticeId.CARBON);
  if (res) {
    if (!reason) {
      res.writeHead(302, {Location: v5Pages.getCareRoot});
      res.end();
      return {} as Props;
    }

    if (maybeNewSlug) {
      res.writeHead(301, {Location: `${v5Pages.getCareRoot}/${maybeNewSlug}`});
      res.end();
      return {} as Props;
    }

    if (!careEntry || (!careEntry.content && !isReasonDiscoverable(reason))) {
      res.writeHead(302, {Location: v5Pages.getCareRoot});
      res.end();
      return {} as Props;
    }

    if (reason && type && type !== "get-care") {
      res.writeHead(301, {Location: `${v5Pages.getCareRoot}/${slug}`});
      res.end();
      return {} as Props;
    }
  }

  if (reason && careEntry?.content) {
    return getInitialPropsReason({reason, reasonContent: careEntry.content, ...context});
  }

  return {} as Props;
};

export default CareItem;
